<template>
  <div class="container">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div class="mt-4 mb-5" style="max-width:700px">
        <h3>Synkroniser Z-rapport (Dagsoppgjør) til Unimicro</h3>

        <div v-show="!loading">
            <div v-if="locations.length === 0" class="mt-4">
              <span class="message-info">Ingen avdelinger har Unimicro aktivert. Aktiver det under Avdelinger.</span>
            </div>

            <div v-else-if="!configured" class="mt-4">
              <span class="message-info">
                {{ stateMessage }}
                <template v-if="stateShowLink">Gå til <a href="/unimicro">Unimicro innstillinger</a>.</template>
              </span>
            </div>

            <div v-if="configured">
                <div v-if="hasMultiLocations()" class="mt-4">
                  <span class="ingredient-title">Velg avdeling</span>
                  <select class="form-select" v-model="locationId" @change="onLocationChange()">
                    <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
                  </select>
                </div>

                <div class="mt-4">
                  <p class="text-muted">Synkroniser dagsoppgjør som bilag i Unimicro.</p>
                  <SelectDate ref="selectDateFrom" label="Velg fra og med dato"/>
                  <SelectDate ref="selectDateTo" label="Velg til og med dato"/>
                  <button type="button" class="bo_btn mt-2" @click="syncZReport"> Synkroniser Z-rapport </button>
                  <button type="button" class="bo_btn mt-2 ms-4" style="background-color:var(--bo-muted)" @click="validateSync"> Valider synkronisering </button>

                  <div v-if="syncCompleted" class="mt-3">
                    <div :class="syncResult.success ? 'message-success' : 'message-error'">
                      <p>{{ syncResult.message }}</p>
                      <p v-if="syncResult.successCount !== undefined">Opprettet: {{ syncResult.successCount }} av {{ syncResult.totalZReports }} | Feil: {{ syncResult.errorCount }}</p>
                    </div>
                    <div v-if="syncResult.errors" class="mt-2 message-error">
                      <span class="ingredient-title">Feildetaljer:</span>
                      <ul>
                        <li v-for="(error, index) in syncResult.errors" :key="index">{{ error }}</li>
                      </ul>
                    </div>
                    <div v-if="syncResult.existingEntries && syncResult.existingEntries.length > 0" class="mt-3">
                      <span class="ingredient-title">Eksisterende bilag i Unimicro:</span>
                      <table class="table table-sm table-bordered mt-1">
                        <thead>
                          <tr>
                            <th>Bnr.</th>
                            <th>Beskrivelse</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(v, index) in syncResult.existingEntries" :key="index">
                            <td>{{ v.journalEntryNumber || v.id }}</td>
                            <td>{{ v.description }}</td>
                            <td><button type="button" class="btn btn-sm btn-outline-warning" @click="creditExistingJournalEntry(v, index)">Krediter</button></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Validation results -->
                  <div v-if="validationCompleted" class="mt-4">
                    <div v-if="!validationResult.success" class="message-error">
                      <p>{{ validationResult.message }}</p>
                    </div>

                    <div v-else>
                      <h5 :class="validationResult.match ? 'text-success' : 'text-danger'">
                        {{ validationResult.match ? 'Alle beløp stemmer' : 'Avvik funnet' }}
                      </h5>
                      <p class="text-muted mb-1">Z-rapporter: {{ validationResult.zReportCount }} | Unimicro bilag: {{ validationResult.voucherCount }}</p>

                      <!-- Payment type comparison -->
                      <span class="ingredient-title">Betalingsmiddel</span>
                      <table class="table table-sm table-bordered mt-1">
                        <thead>
                          <tr>
                            <th></th>
                            <th>CAGISTA</th>
                            <th>Unimicro</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in paymentRows" :key="row.label">
                            <td :class="row.isTotal ? 'fw-bold' : ''">{{ row.label }}</td>
                            <td :class="row.isTotal ? 'fw-bold' : ''">{{ formatAmount(row.cagista) }}</td>
                            <td :class="row.isTotal ? 'fw-bold' : ''">{{ formatAmount(row.unimicro) }}</td>
                            <td>
                              <span v-if="amountsMatch(row.cagista, row.unimicro)" class="text-success fw-bold">OK</span>
                              <span v-else class="text-danger fw-bold">{{ formatAmount(row.unimicro - row.cagista) }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <!-- VAT breakdown comparison -->
                      <span class="ingredient-title mt-3">MVA-fordeling</span>
                      <table class="table table-sm table-bordered mt-1">
                        <thead>
                          <tr>
                            <th>MVA%</th>
                            <th>Brutto (CAGISTA)</th>
                            <th>Brutto (Unimicro)</th>
                            <th>MVA (CAGISTA)</th>
                            <th>MVA (Unimicro)</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="row in vatRows" :key="row.vatRate">
                            <td>{{ row.vatRate }}%</td>
                            <td>{{ formatAmount(row.caBrutto) }}</td>
                            <td>{{ formatAmount(row.umBrutto) }}</td>
                            <td>{{ formatAmount(row.caVat) }}</td>
                            <td>{{ formatAmount(row.umVat) }}</td>
                            <td>
                              <span v-if="amountsMatch(row.caBrutto, row.umBrutto)" class="text-success fw-bold">OK</span>
                              <span v-else class="text-danger fw-bold">{{ formatAmount(row.umBrutto - row.caBrutto) }}</span>
                            </td>
                          </tr>
                          <tr v-if="vatRows.length > 0" class="fw-bold">
                            <td>Total</td>
                            <td>{{ formatAmount(vatTotals.caBrutto) }}</td>
                            <td>{{ formatAmount(vatTotals.umBrutto) }}</td>
                            <td>{{ formatAmount(vatTotals.caVat) }}</td>
                            <td>{{ formatAmount(vatTotals.umVat) }}</td>
                            <td>
                              <span v-if="amountsMatch(vatTotals.caBrutto, vatTotals.umBrutto)" class="text-success fw-bold">OK</span>
                              <span v-else class="text-danger fw-bold">{{ formatAmount(vatTotals.umBrutto - vatTotals.caBrutto) }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <!-- Cash rounding info -->
                      <div v-if="validationResult.cashRoundingMessage" class="mt-2 alert alert-info mb-0 py-1 px-2">
                        {{ validationResult.cashRoundingMessage }}
                      </div>

                      <!-- Differences detail -->
                      <div v-if="validationResult.differences && validationResult.differences.length > 0" class="mt-2 message-error">
                        <span class="ingredient-title">Avvik:</span>
                        <ul class="mb-0" style="list-style-type: disc; padding-left: 20px;">
                          <li v-for="(diff, index) in validationResult.differences" :key="index" style="display: list-item; float: none; margin-bottom: 4px;">{{ diff }}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
        </div>
      </div>
    <ConfirmDialogue ref="confirmDialogue"/>
  </div>
</template>

<script>
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import SelectDate from "@/components/other/SelectDate";
  import ConfirmDialogue from "@/components/other/ConfirmDialogue";
  import SettingsService from "@/services/settings/SettingsService";
  import UnimicroState from "@/util/UnimicroState";
  import LocationUtil from "@/util/LocationUtil";
  import LocationService from "@/services/location/LocationService";

  export default {
    components: {LoadingSpinner, SelectDate, ConfirmDialogue},
    data() {
      return {
        loading: true,
        locationId: '',
        locations: [],
        configured: false,
        stateMessage: '',
        stateShowLink: false,
        syncCompleted: false,
        syncResult: {},
        validationCompleted: false,
        validationResult: {}
      };
    },
    computed: {
      paymentRows() {
        if (!this.validationResult.cagista) return [];
        const ca = this.validationResult.cagista;
        const um = this.validationResult.unimicro;
        let rows = [];
        if (ca.cash > 0 || um.cash > 0) rows.push({ label: 'Kontant', cagista: ca.cash, unimicro: um.cash });
        if (ca.card > 0 || um.card > 0) rows.push({ label: 'Kort', cagista: ca.card, unimicro: um.card });
        if (ca.bankAxept > 0 || um.bankAxept > 0) rows.push({ label: 'BankAxept', cagista: ca.bankAxept, unimicro: um.bankAxept });
        if (ca.vipps > 0 || um.vipps > 0) rows.push({ label: 'Vipps', cagista: ca.vipps, unimicro: um.vipps });
        if (ca.bonus > 0 || um.bonus > 0) rows.push({ label: 'Bonuspoeng', cagista: ca.bonus, unimicro: um.bonus });
        rows.push({ label: 'Total salg', cagista: ca.totalSales, unimicro: um.totalSales, isTotal: true });
        if (ca.tips > 0 || um.tips > 0) rows.push({ label: 'Tips', cagista: ca.tips, unimicro: um.tips });
        return rows;
      },
      vatRows() {
        if (!this.validationResult.cagista) return [];
        const caBreakdown = this.validationResult.cagista.vatBreakdown || [];
        const umBreakdown = this.validationResult.unimicro.vatBreakdown || [];

        // Merge VAT rates from both sources
        const rates = new Set();
        caBreakdown.forEach(v => rates.add(v.vatRate));
        umBreakdown.forEach(v => rates.add(v.vatRate));

        return Array.from(rates).sort((a, b) => a - b).map(rate => {
          const ca = caBreakdown.find(v => v.vatRate === rate) || { brutto: 0, vat: 0, netto: 0 };
          const um = umBreakdown.find(v => v.vatRate === rate) || { brutto: 0, vat: 0, netto: 0 };
          return {
            vatRate: rate,
            caBrutto: ca.brutto,
            umBrutto: um.brutto,
            caVat: ca.vat,
            umVat: um.vat
          };
        });
      },
      vatTotals() {
        return this.vatRows.reduce((t, r) => ({
          caBrutto: t.caBrutto + (r.caBrutto || 0),
          umBrutto: t.umBrutto + (r.umBrutto || 0),
          caVat: t.caVat + (r.caVat || 0),
          umVat: t.umVat + (r.umVat || 0)
        }), { caBrutto: 0, umBrutto: 0, caVat: 0, umVat: 0 });
      }
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data.filter(l => l.accountingSystem === 'UNIMICRO');
            this.locationId = this.locations.length ? this.locations[0].id : '';

            if (this.locationId) {
              this.checkAccountConfigured();
            } else {
              this.configured = false;
              this.loading = false;
            }
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      onLocationChange(){
        this.syncCompleted = false;
        this.validationCompleted = false;
        this.checkAccountConfigured();
      },
      checkAccountConfigured(){
        UnimicroState.load(this.locationId).then((state) => {
          this.configured = state.active;
          this.stateMessage = state.message;
          this.stateShowLink = state.showLink;
          this.loading = false;
        });
      },
      syncZReport(){
        this.loading = true;
        this.syncCompleted = false;
        this.validationCompleted = false;
        let fromDate = this.$refs.selectDateFrom.getSelectedDate();
        let toDate = this.$refs.selectDateTo.getSelectedDate();

        SettingsService.syncZReportToUnimicro(this.locationId, fromDate, toDate).then(
            (response) => {
              this.syncCompleted = true;
              this.syncResult = response.data;
              this.loading = false;
            },
            (error) => {
              this.syncCompleted = true;
              this.syncResult = { success: false, message: 'Feil ved Z-rapport synkronisering' };
              console.log(error);
              this.loading = false;
            }
        );
      },
      async creditExistingJournalEntry(journalEntry, index){
        const ok = await this.$refs.confirmDialogue.show({
          title: 'Krediter bilag',
          message: 'Er du sikker på at du vil kreditere (reversere) bilag Bnr. ' + (journalEntry.journalEntryNumber || journalEntry.id) + ' (' + journalEntry.description + ')? Det opprettes et nytt motbilag i Unimicro.',
          okButton: 'Krediter',
          cancelButton: 'Avbryt'
        });
        if (!ok) return;
        this.loading = true;

        SettingsService.deleteUnimicroSingleJournalEntry(this.locationId, journalEntry.id).then(
            (response) => {
              if (response.data.success) {
                this.syncResult.existingEntries.splice(index, 1);
                if (this.syncResult.existingEntries.length === 0) {
                  this.syncCompleted = false;
                }
              } else {
                this.syncResult.message = response.data.message || 'Feil ved kreditering av bilag';
              }
              this.loading = false;
            },
            (error) => {
              this.syncResult.message = 'Feil ved kreditering av bilag';
              console.log(error);
              this.loading = false;
            }
        );
      },
      validateSync(){
        this.loading = true;
        this.syncCompleted = false;
        this.validationCompleted = false;
        let fromDate = this.$refs.selectDateFrom.getSelectedDate();
        let toDate = this.$refs.selectDateTo.getSelectedDate();

        SettingsService.validateUnimicroZReportSync(this.locationId, fromDate, toDate).then(
            (response) => {
              this.validationCompleted = true;
              this.validationResult = response.data;
              this.loading = false;
            },
            (error) => {
              this.validationCompleted = true;
              this.validationResult = { success: false, message: 'Feil ved validering' };
              console.log(error);
              this.loading = false;
            }
        );
      },
      formatAmount(amount) {
        if (amount === 0 || amount === undefined) return '0,00';
        return amount.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      amountsMatch(a, b) {
        let tolerance = Math.max(0.02, (this.validationResult.zReportCount || 1) * 0.01);
        if (this.validationResult.cashRounding) {
          tolerance = Math.max(tolerance, 1.0);
        }
        return Math.abs((a || 0) - (b || 0)) <= tolerance;
      }
    }
  };
</script>
